import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 't24_user_votes';

/**
 * Hook for voting operations with LocalStorage persistence
 */
export function useVoting() {
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState(null);
  const [userVotes, setUserVotes] = useState(() => {
    // Load votes from LocalStorage on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load votes from localStorage:', err);
      return {};
    }
  });

  // Persist votes to LocalStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userVotes));
    } catch (err) {
      console.error('Failed to save votes to localStorage:', err);
    }
  }, [userVotes]);

  // Submit a vote with optimistic UI update
  const submitVote = useCallback(async (proposalId, voteType, walletAddress = null) => {
    // Check if user is logged in
    if (!window.t24Governance.isLoggedIn) {
      throw new Error(window.t24Governance.strings.loginToVote);
    }

    // Store previous vote for rollback on error
    const previousVote = userVotes[proposalId] || null;

    try {
      setVoting(true);
      setError(null);

      // OPTIMISTIC UPDATE: Immediately update UI before server response
      setUserVotes((prev) => ({
        ...prev,
        [proposalId]: voteType,
      }));

      const formData = new FormData();
      formData.append('action', 't24_vote_on_proposal');
      formData.append('nonce', window.t24Governance.nonce);
      formData.append('proposal_id', proposalId);
      formData.append('vote_type', voteType);
      if (walletAddress) {
        formData.append('wallet_address', walletAddress);
      }

      const response = await fetch(window.t24Governance.ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to submit vote');
      }

      const data = await response.json();

      if (data.success) {
        // Server confirmed - vote is already updated in UI
        return data.data;
      } else {
        throw new Error(data.data?.message || window.t24Governance.strings.voteError);
      }
    } catch (err) {
      console.error('Error submitting vote:', err);
      setError(err.message);

      // ROLLBACK: Restore previous vote state on error
      setUserVotes((prev) => {
        if (previousVote === null) {
          // Remove the vote if there wasn't one before
          const { [proposalId]: _, ...rest } = prev;
          return rest;
        } else {
          // Restore previous vote
          return {
            ...prev,
            [proposalId]: previousVote,
          };
        }
      });

      throw err;
    } finally {
      setVoting(false);
    }
  }, [userVotes]);

  // Vote FOR a proposal
  const voteFor = useCallback(
    async (proposalId, walletAddress = null) => {
      return submitVote(proposalId, 'for', walletAddress);
    },
    [submitVote]
  );

  // Vote AGAINST a proposal
  const voteAgainst = useCallback(
    async (proposalId, walletAddress = null) => {
      return submitVote(proposalId, 'against', walletAddress);
    },
    [submitVote]
  );

  // ABSTAIN from voting
  const abstain = useCallback(
    async (proposalId, walletAddress = null) => {
      return submitVote(proposalId, 'abstain', walletAddress);
    },
    [submitVote]
  );

  // Get user's vote for a proposal
  const getUserVote = useCallback(
    (proposalId) => {
      return userVotes[proposalId] || null;
    },
    [userVotes]
  );

  // Check if user has voted on a proposal
  const hasVoted = useCallback(
    (proposalId) => {
      return proposalId in userVotes;
    },
    [userVotes]
  );

  // Calculate vote percentages
  const calculateVotePercentages = useCallback((votesFor, votesAgainst, votesAbstain) => {
    const total = votesFor + votesAgainst + votesAbstain;

    if (total === 0) {
      return {
        forPercent: 0,
        againstPercent: 0,
        abstainPercent: 0,
      };
    }

    return {
      forPercent: Math.round((votesFor / total) * 100),
      againstPercent: Math.round((votesAgainst / total) * 100),
      abstainPercent: Math.round((votesAbstain / total) * 100),
    };
  }, []);

  // Calculate if quorum is reached
  const isQuorumReached = useCallback((totalVotes, quorumRequired) => {
    return totalVotes >= quorumRequired;
  }, []);

  return {
    voting,
    error,
    userVotes,
    submitVote,
    voteFor,
    voteAgainst,
    abstain,
    getUserVote,
    hasVoted,
    calculateVotePercentages,
    isQuorumReached,
  };
}
